// lib/utils.ts
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

import { getLanguageCookie } from "@/data/cookieClient";
import { LanguageType, UserProfile } from "./types";

import { NextResponse } from "next/server";
// app/utils/getTranslation.t


import translationsJson from "@/data/translations.json"; // Import your JSON

// Define the type for translations
type Translation = {
  [key: string]: [string, string]; // Each key maps to a tuple of two strings
};

export function getWhatsAppLink(phone: string): string {
  // Remove all non-digit characters
  const digitsOnly = phone.replace(/\D/g, "");

  let internationalNumber = digitsOnly;

  // If it starts with 0 and is Saudi local (e.g. 05xxxxxxx), replace with 966
  if (/^0\d{9}$/.test(digitsOnly)) {
    internationalNumber = `966${digitsOnly.slice(1)}`;
  }

  // If it starts with 00966, convert to 966
  if (internationalNumber.startsWith("00966")) {
    internationalNumber = internationalNumber.replace(/^00966/, "966");
  }

  // If it starts with +966, convert to 966
  if (internationalNumber.startsWith("9660")) {
    // Remove leading 0 after 966 (just in case someone wrote +9660557...)
    internationalNumber = "966" + internationalNumber.slice(4);
  }

  return `https://wa.me/${internationalNumber}`;
}

// Assert the JSON type
const translations = translationsJson as unknown as Translation;

// Function to fetch a translation

export const _t = (key: string, lang?: LanguageType): string => {
  // Use the provided language or fallback to app state

  const currentLanguage =
    lang || (typeof window !== "undefined" ? getLanguageCookie() : "en-US");

  const translation = translations[key];

  // console.log(`Type of window: ${typeof window}`);
  // console.log(`CurrentLanguage: ${currentLanguage}`);

  if (translation) {
    const langIndex = currentLanguage === "en-US" ? 0 : 1;
    // console.log(`Translation: ${translation[langIndex]}`);
    return translation[langIndex];
  }

  // Fallback to the key if no translation is found
  return key;
};

export function setCORSHeaders(response: NextResponse) {
  response.headers.set("Access-Control-Allow-Origin", "*"); // Adjust as needed
  response.headers.set(
    "Access-Control-Allow-Methods",
    "GET, POST, PUT, DELETE, OPTIONS"
  );
  response.headers.set(
    "Access-Control-Allow-Headers",
    "Content-Type, Authorization"
  );

  return response;
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "PKR",
});

export function stripHtml(html: string) {
  return html.replace(/<[^>]*>?/gm, "");
}

export const formatCurrency = (
  amount: number,
  showCurrency: boolean = true
): string => {
  const options: Intl.NumberFormatOptions = {
    style: showCurrency ? "currency" : "decimal",
    currency: "PKR",
    minimumFractionDigits: 2, // Set minimum fraction digits to 2
    maximumFractionDigits: 2, // Set maximum fraction digits to 2
    // You can add other options here if needed
  };

  const formatter = new Intl.NumberFormat("en-US", options);
  return formatter.format(amount);
};

export const slugify = (text: string) => {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-") // Replace spaces with -
    .replace(/[^\w\-]+/g, "") // Remove all non-word chars
    .replace(/\-\-+/g, "-"); // Replace multiple - with single -
};

// export const storeUrl = (): string => {
//   // Function to get the server's IP address
//   const getServerIp = (): string => {
//     const networkInterfaces = os.networkInterfaces();
//     for (const interfaceName in networkInterfaces) {
//       for (const iface of networkInterfaces[interfaceName] || []) {
//         if ("IPv4" === iface.family && !iface.internal) {
//           return iface.address;
//         }
//       }
//     }
//     return "localhost";
//   };

//   // Providing a default URL to ensure a string is always returned
//   const defaultUrl = "http://localhost:3000";

//   let url: string;

//   if (process.env.NODE_ENV === "development") {
//     const serverIp = getServerIp();
//     url = `http://${serverIp}:3000`;
//   } else if (process.env.NODE_ENV === "production") {
//     url = process.env.NEXT_PUBLIC_LIVE_STORE_URL || defaultUrl;
//   } else {
//     url = process.env.NEXT_PUBLIC_REMOTE_STORE_URL || defaultUrl;
//   }

//   return url;
// };

export const generateWhatsAppLink = (mobile: string, message: string) => {
  const encodedMessage = encodeURIComponent(message);
  return `https://api.whatsapp.com/send?phone=${mobile.replace(
    "+",
    ""
  )}&text=${encodedMessage}`;
};

// // Configure AWS to use promise
// AWS.config.setPromisesDependency(Promise);

// // Initialize the AWS credentials
// AWS.config.update({
//   accessKeyId: process.env.AWS_ACCESS_KEY_ID,
//   secretAccessKey: process.env.AWS_SECRET_ACCESS_KEY,
//   region: process.env.AWS_REGION,
// });

// // Create an S3 instance
// const s3 = new AWS.S3();

// // Function to upload a file to S3
// export const uploadFileToS3 = async (file: File): Promise<string> => {
//   const fileName = `${Date.now()}_${file.name}`;
//   const params: AWS.S3.PutObjectRequest = {
//     Bucket: process.env.AWS_S3_BUCKET_NAME!,
//     Key: fileName,
//     Body: file,
//     ContentType: file.type,
//     ACL: 'public-read', // or 'private' depending on your needs
//   };

//   return s3.upload(params).promise().then(data => data.Location);
// };

export function isValidSalePrice(salePrice: number): boolean {
  return salePrice > 0;
}

//Order Helpers

export enum OrderStatus {
  Pending = "PENDING",
  PaymentReceived = "PAYMENT_RECEIVED",
  Processing = "PROCESSING",
  Packed = "PACKED",
  Shipped = "SHIPPED",
  OutForDelivery = "OUT_FOR_DELIVERY",
  Delivered = "DELIVERED",
  Cancelled = "CANCELLED",
  Returned = "RETURNED",
  Refunded = "REFUNDED",
  Failed = "FAILED",
}

export const OrderStatusDescriptions: { [key in OrderStatus]: string } = {
  [OrderStatus.Pending]: "Order has been received but not yet processed.",
  [OrderStatus.PaymentReceived]:
    "Payment for the order has been received and confirmed.",
  [OrderStatus.Processing]: "Order is being prepared for shipment.",
  [OrderStatus.Packed]: "Order has been packed and is ready for shipping.",
  [OrderStatus.Shipped]: "Order has been shipped and is in transit.",
  [OrderStatus.OutForDelivery]: "Order is out for delivery.",
  [OrderStatus.Delivered]: "Order has been delivered to the customer.",
  [OrderStatus.Cancelled]: "Order has been cancelled.",
  [OrderStatus.Returned]: "Order has been returned by the customer.",
  [OrderStatus.Refunded]: "A refund has been issued for the order.",
  [OrderStatus.Failed]: "There was a failure in the order process.",
};

export function getStatusDescription(status: OrderStatus): string {
  return OrderStatusDescriptions[status];
}

// Payment Helpers

export enum PaymentStatus {
  Pending = "PENDING",
  Success = "SUCCESS",
  Failed = "FAILED",
  Refunded = "REFUNDED",
  PartiallyRefunded = "PARTIALLY_REFUNDED",
  Cancelled = "CANCELLED",
  Disputed = "DISPUTED",
  Processing = "PROCESSING",
}

export const PaymentStatusDescriptions: { [key in PaymentStatus]: string } = {
  [PaymentStatus.Pending]: "Payment is pending confirmation.",
  [PaymentStatus.Success]: "Payment has been successfully received.",
  [PaymentStatus.Failed]: "Payment has failed.",
  [PaymentStatus.Refunded]: "Payment has been fully refunded.",
  [PaymentStatus.PartiallyRefunded]: "Payment has been partially refunded.",
  [PaymentStatus.Cancelled]: "Payment has been cancelled.",
  [PaymentStatus.Disputed]: "Payment is disputed.",
  [PaymentStatus.Processing]: "Payment is being processed.",
};

export function getPaymentStatusDescription(status: PaymentStatus): string {
  return PaymentStatusDescriptions[status];
}

// Example usage
// console.log(getPaymentStatusDescription(PaymentStatus.Success)); // Outputs: "Payment has been successfully received."

// const prisma = new PrismaClient();

// // Fetches the last number used for order IDs
// async function getLastNumberUsed(): Promise<number> {
//   const counter = await prismadb.counter.findUnique({ where: { id: 'orderCounter' } });

// }

// // Updates the counter with the new last number used
// async function updateLastNumberUsed(newNumber: number): Promise<void> {
//   await prismadb.counter.upsert({
//     where: { id: 'orderCounter' },
//     update: { lastOrderNumber: newNumber },
//     create: { id: 'orderCounter', lastOrderNumber: newNumber },
//   });
// }

export function translate(
  language: LanguageType,
  english: string,
  arabic: string
): string {
  return language === LanguageType.AR ? arabic : english;
}

type EnumOptionSettings = {
  preserveUnderscore?: boolean;
  uppercase?: boolean;
};

export function toTitleCase(str: string): string {
  return str
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function enumToSortedOptions<T extends string>(
  enumObj: Record<string, T>,
  settings: EnumOptionSettings = {}
) {
  const { preserveUnderscore = false, uppercase = false } = settings;

  return Object.values(enumObj)
    .map((value, index) => {
      let label = String(value);

      // Special handling for blood types (e.g., A_POS → A+)
      label = label
        .replace("_POS", "+")
        .replace("_NEG", "-");

      if (!preserveUnderscore) {
        label = label.replace(/_/g, " ");
      }

      if (uppercase) {
        label = label.toUpperCase();
      } else {
        label = toTitleCase(label);
      }

      return {
        id: index,
        value,
        name: label,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

const positionRank = (position?: string | null) => {
  const lower = (position || "").toLowerCase();
  if (lower.includes("manager")) return 1;
  if (lower.includes("supervisor")) return 2;
  if (lower.includes("senior")) return 3;
  if (lower.includes("junior")) return 4;
  if (lower.includes("engineer")) return 5;
  return 6;
};

export const sortByPosition = (a: UserProfile, b: UserProfile) => {
  const rankA = positionRank(a.position);
  const rankB = positionRank(b.position);

  if (rankA !== rankB) return rankA - rankB;
  return (a.position || "").localeCompare(b.position || "");
};

